import { Checkbox } from '@/components/Checkbox';
import { InputSlider } from '@/components/SceneTab/Menus/InputSlider';
import {
  disableImageRibbon,
  enableImageRibbon,
  setViewer2DSettings,
  useViewer,
} from '@/stores/viewer';
import { cn } from '@/utils/classname';
import {
  Button,
  chip,
  chipIcon,
  Input,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@skand/ui';
import { useState } from 'react';

export const Viewer2DMenu = () => {
  const viewer2DSettings = useViewer(state => state.viewer2DSettings);
  const enabledImageRibbon = useViewer(state => state.enabledImageRibbon);
  const targetPhoto = useViewer(state => state.targetPhoto);

  const [count, setCount] = useState(viewer2DSettings.count);
  const [showAll, setShowAll] = useState(viewer2DSettings.showAll);
  const [gapSize, setGapSize] = useState(viewer2DSettings.gapSize);

  // Handle updating the image count, automatically resetting the show all flag
  const handleChangeCount = (count: number) => {
    setCount(count);
    setShowAll(false);
  };

  // Handle toggling the image ribbon
  const handleToggleRibbon = (value: boolean) => {
    if (value) {
      enableImageRibbon(targetPhoto);
    } else {
      disableImageRibbon();
    }
  };

  // Handle applying settings that affect count and sort
  const handleApply = () => {
    setViewer2DSettings({ count, showAll, gapSize });
  };

  // Check if any of the deferred update settings have changed
  const isDirty =
    count !== viewer2DSettings.count ||
    showAll !== viewer2DSettings.showAll ||
    gapSize !== viewer2DSettings.gapSize;

  return (
    <div className={'w-full h-full'}>
      <div className={cn('flex', 'flex-col', 'justify-between', 'items-start', 'mt-12px')}>
        <span className={cn('typo-text-s-em', 'color-neutral-800', 'mb-2')}>
          Show number of images
        </span>
        <div className="w-full flex flex-row items-center justify-between gap-4">
          <div className="flex-1">
            <Input
              onChange={value => handleChangeCount(value.length ? parseInt(value) : 0)}
              type="number"
              value={count}
            />
          </div>
          <div className="flex flex-row gap-2">
            <Checkbox checked={showAll} setToggleCheckbox={() => setShowAll(!showAll)} />
            <p className={cn('typo-text-small text-neutral-800')}>All</p>
          </div>
        </div>
      </div>

      <div className={cn('flex', 'flex-row', 'justify-between', 'items-start')}>
        <InputSlider
          className={'typo-text-s-em w-full'}
          max={1000}
          min={0}
          setValue={setGapSize}
          step={1}
          title="Minimum gap distance (m)"
          value={gapSize}
        />
      </div>

      <Button
        className="mt-12px w-full"
        disabled={!isDirty}
        filled
        onClick={handleApply}
        primary
        size="s"
      >
        Apply
      </Button>

      <div className={cn('border-1px', 'border-solid', 'border-b-neutral-300', 'mt-12px')} />

      <div className={cn('flex', 'flex-row', 'justify-between', 'items-start', 'mt-12px')}>
        <span className={cn('typo-text-s-em', 'color-neutral-800')}>Lock sorting</span>
        <Switch
          checked={viewer2DSettings.lockSorting}
          onChange={value => setViewer2DSettings({ lockSorting: value })}
        />
      </div>

      <div className={cn('flex', 'flex-row', 'justify-between', 'items-start', 'mt-12px')}>
        <span className={cn('typo-text-s-em', 'color-neutral-800')}>Enable ribbon</span>
        <Switch checked={enabledImageRibbon} onChange={handleToggleRibbon} />
      </div>

      <div className={cn('flex', 'flex-row', 'justify-between', 'items-start', 'mt-12px')}>
        <div className={cn('flex', 'flex-row', 'items-center')}>
          <span className={cn('typo-text-s-em', 'color-neutral-800')}>Preserve view</span>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  'i-skand-info',
                  'color-neutral-500',
                  'ml-2',
                  'text-3.5',
                  'cursor-pointer',
                )}
              />
            </TooltipTrigger>
            <TooltipContent>
              <div className={chip({ intent: 'info' })}>
                <div className={chipIcon({ intent: 'info' })} />
                Keep where the camera is looking when switching between images in the ribbon.
              </div>
            </TooltipContent>
          </Tooltip>
        </div>
        <Switch
          checked={viewer2DSettings.preserveView}
          onChange={preserveView => setViewer2DSettings({ preserveView })}
        />
      </div>

      <div className={cn('flex', 'flex-row', 'justify-between', 'items-center', 'mt-12px')}>
        <span className={cn('typo-text-s-em', 'color-neutral-800')}>Panorama icon size</span>
        <input
          className={cn(
            'flex',
            'justify-center',
            'text-center',
            'border-solid',
            'border-neutral-400',
            'b-1px',
            'rounded-4px',
            'w-36px',
            'h-28px',
            'typo-text-small',
            'text-neutral-700',
          )}
          onChange={e =>
            setViewer2DSettings({ panoramaIconSize: Math.max(0, parseFloat(e.target.value)) })
          }
          step={0.1}
          type="number"
          value={viewer2DSettings.panoramaIconSize}
        />
      </div>
      <div className={cn('flex', 'flex-row', 'justify-between', 'items-center', 'mt-12px')}>
        <span className={cn('typo-text-s-em', 'color-neutral-800')}>Panorama camera height</span>
        <input
          className={cn(
            'flex',
            'justify-center',
            'text-center',
            'border-solid',
            'border-neutral-400',
            'b-1px',
            'rounded-4px',
            'w-36px',
            'h-28px',
            'typo-text-small',
            'text-neutral-700',
          )}
          onChange={e =>
            setViewer2DSettings({ panoramaCameraHeight: Math.max(0, parseFloat(e.target.value)) })
          }
          step={0.1}
          type="number"
          value={viewer2DSettings.panoramaCameraHeight}
        />
      </div>
    </div>
  );
};
